import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Spinner, Alert } from 'react-bootstrap';
import { FaCalendarAlt, FaEdit, FaEye, FaFileUpload, FaSyncAlt, FaStamp } from 'react-icons/fa';
import { Search, PersonPlus, ArrowClockwise } from 'react-bootstrap-icons';
import DashboardLayout from '../components/DashboardLayout';
import { apiRequest } from '../utils/api';
import { getActiveCompanyId } from '../utils/companyContext';
import '../assets/styles/files.css';
import '../assets/styles/employees.css';
import useOutletSearch from '../hooks/useOutletSearch';
import useDocumentTypes from '../hooks/useDocumentTypes';
import { getModulePermissions, isDirectEmployeeAdmin } from '../utils/permissions';

const getTodayInputValue = () => new Date().toISOString().slice(0, 10);

const MAX_EMPLOYEE_FILE_SIZE = 10 * 1024 * 1024;
const ALLOWED_EMPLOYEE_FILE_MIME_TYPES = ['application/pdf', 'image/jpeg', 'image/png'];
const ALLOWED_EMPLOYEE_FILE_EXTENSIONS = ['pdf', 'jpg', 'jpeg', 'png'];

const normalizeStatus = (value) => (value || '').toString().trim().toLowerCase();

const STATUS_MODAL_DEFAULTS = {
  open: false,
  employee: null,
  status: 'active',
  activationReason: 'rejoined',
  activationDescription: '',
  activationDate: getTodayInputValue(),
  inactivationReason: 'relieving',
  inactivationDescription: '',
  inactivationDate: '',
  leaveFromDate: '',
  document: null,
};

const Employees = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [employees, setEmployees] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [activeCompanyId, setActiveCompanyId] = useState(() => getActiveCompanyId());
  const [selectedOutlet, setSelectedOutlet] = useState('');
  const [isOutletSelectionLocked, setOutletSelectionLocked] = useState(false);
  const [statusFilter, setStatusFilter] = useState(() => searchParams.get('status') || 'active');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const joinMonth = searchParams.get('joinMonth');
  const joinYear = searchParams.get('joinYear');
  const hasJoinFilter = Boolean(joinMonth && joinYear);
  const joinFilterLabel = hasJoinFilter ? new Date(Number(joinYear), Number(joinMonth) - 1, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' }) : '';
  const clearJoinFilter = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('joinMonth');
    next.delete('joinYear');
    if (next.get('status') === 'all') next.delete('status');
    setSearchParams(next, { replace: true });
    setStatusFilter(next.get('status') || 'active');
    setCurrentPage(1);
  };
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 10,
    totalPages: 0,
    totalRecords: 0,
  });
  const [statusModal, setStatusModal] = useState(() => ({ ...STATUS_MODAL_DEFAULTS }));
  const [statusSubmitting, setStatusSubmitting] = useState(false);
  const [statusError, setStatusError] = useState(null);
  const [fileUploadModal, setFileUploadModal] = useState({ open: false, employee: null });
  const [fileUploadForm, setFileUploadForm] = useState({ documentTypeId: '', file: null });
  const [fileUploadSubmitting, setFileUploadSubmitting] = useState(false);
  const [fileUploadError, setFileUploadError] = useState('');
  const [fileUploadSuccess, setFileUploadSuccess] = useState('');
  const employeeListPermissions = useMemo(() => getModulePermissions('employee.list'), []);
  const employeeViewPermissions = useMemo(() => getModulePermissions('employee.view'), []);
  const employeeCreateEditPermissions = useMemo(() => getModulePermissions('employee.create_edit'), []);
  const approvalPendingPermissions = useMemo(() => getModulePermissions('approvals.pending'), []);
  const employeeFilePermissions = useMemo(() => getModulePermissions('employee.files'), []);
  const attendanceReportPermissions = useMemo(() => getModulePermissions('attendance_report'), []);
  const { documentTypes, isLoading: isLoadingDocumentTypes, error: documentTypeError } = useDocumentTypes({ activeOnly: true });
  const {
    searchTerm: outletSearchTerm,
    setSearchTerm: setOutletSearchTerm,
    options: outletOptions,
    loading: isOutletSearching,
    error: outletSearchError,
    search: searchOutlets,
    clearOptions: clearOutletOptions,
  } = useOutletSearch({ navigate, companyId: activeCompanyId, enabled: !isOutletSelectionLocked });

  const loadEmployees = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      if (!activeCompanyId) {
        setEmployees([]);
        setPagination((prev) => ({ ...prev, totalRecords: 0, totalPages: 0 }));
        setError('Please select a company to view employees.');
        return;
      }

      const params = new URLSearchParams();
      params.set('page', String(currentPage));
      params.set('pageSize', String(pageSize));
      params.set('status', statusFilter);
      if (joinMonth && joinYear) {
        params.set('joinMonth', joinMonth);
        params.set('joinYear', joinYear);
      }

      if (activeCompanyId) {
        params.set('companyId', activeCompanyId);
      }

      if (selectedOutlet) {
        params.set('outletId', selectedOutlet);
      }

      if (debouncedSearchTerm.trim()) {
        params.set('search', debouncedSearchTerm.trim());
      }

      const response = await apiRequest(`/api/admin/employees?${params.toString()}`, {}, navigate);
      const employeesData = Array.isArray(response.data?.employees)
        ? response.data.employees
        : Array.isArray(response.data)
          ? response.data
          : [];

      const paginationData = response.data?.pagination ?? {};
      const totalRecords =
        typeof paginationData.totalRecords === 'number' ? paginationData.totalRecords : employeesData.length;
      const resolvedPageSize = paginationData.pageSize || pageSize;
      const totalPages = totalRecords > 0 ? Math.ceil(totalRecords / resolvedPageSize) : 0;

      setEmployees(employeesData);
      setPagination({
        currentPage: paginationData.currentPage || currentPage,
        pageSize: resolvedPageSize,
        totalPages,
        totalRecords,
      });
      setCurrentPage(paginationData.currentPage || currentPage);
      setPageSize(resolvedPageSize);
    } catch (err) {
      setError(err.message || 'Failed to load employees');
      setEmployees([]);
      setPagination((prev) => ({ ...prev, totalRecords: 0, totalPages: 0 }));
    } finally {
      setLoading(false);
    }
  }, [activeCompanyId, currentPage, debouncedSearchTerm, navigate, pageSize, selectedOutlet, statusFilter, joinMonth, joinYear]);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 400);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [searchTerm]);

  useEffect(() => {
    loadEmployees();
  }, [loadEmployees]);

  useEffect(() => {
    const handleActiveCompanyChanged = (event) => {
      const nextCompanyId = event?.detail?.id ?? getActiveCompanyId();
      setActiveCompanyId(nextCompanyId);
      setSelectedOutlet('');
      setOutletSearchTerm('');
      clearOutletOptions();
      setOutletSelectionLocked(false);
      setCurrentPage(1);
    };

    window.addEventListener('activeCompanyChanged', handleActiveCompanyChanged);

    return () => {
      window.removeEventListener('activeCompanyChanged', handleActiveCompanyChanged);
    };
  }, []);

  const openStatusModal = useCallback((employeeRow) => {
    const normalizedStatus = (employeeRow.statusKey || 'inactive') === 'active' ? 'active' : 'inactive';
    const normalizedReason = ['relieving', 'long_leave', 'others'].includes(employeeRow.inactiveReason)
      ? employeeRow.inactiveReason
      : 'relieving';
    setStatusModal({
      open: true,
      employee: employeeRow,
      status: normalizedStatus,
      activationReason: 'rejoined',
      activationDescription: '',
      activationDate: getTodayInputValue(),
      inactivationReason: normalizedReason,
      inactivationDescription: normalizedStatus === 'inactive' ? employeeRow.inactiveNotes || '' : '',
      inactivationDate:
        normalizedStatus === 'inactive' && employeeRow.relievingDate
          ? String(employeeRow.relievingDate).slice(0, 10)
          : '',
      leaveFromDate: normalizedReason === 'long_leave' ? employeeRow.relievingDate || '' : '',
      document: null,
    });
    setStatusError(null);
  }, []);

  const closeStatusModal = useCallback(() => {
    setStatusModal({ ...STATUS_MODAL_DEFAULTS, activationDate: getTodayInputValue() });
    setStatusError(null);
  }, []);

  const handleStatusSubmit = useCallback(
    async (event) => {
      event.preventDefault();
      if (!statusModal.employee) {
        return;
      }

      setStatusError(null);

      const isInactive = statusModal.status === 'inactive';
      const isActive = statusModal.status === 'active';
      const requiresDocument = isInactive && statusModal.inactivationReason !== 'others';

      if (isActive) {
        if (!statusModal.activationReason) {
          setStatusError('Activation reason is required.');
          return;
        }

        if (!statusModal.activationDate) {
          setStatusError('Activation date is required.');
          return;
        }

        if (
          statusModal.activationReason === 'others' &&
          !statusModal.activationDescription.trim()
        ) {
          setStatusError('Please provide a description for the activation reason.');
          return;
        }
      }

      if (isInactive) {
        if (!statusModal.inactivationReason) {
          setStatusError('Inactivation reason is required.');
          return;
        }

        if (!statusModal.inactivationDate) {
          setStatusError('Inactivation date is required.');
          return;
        }

        if (statusModal.inactivationReason === 'long_leave' && !statusModal.leaveFromDate) {
          setStatusError('Leave from date is required for long leave.');
          return;
        }

        if (
          statusModal.inactivationReason === 'others' &&
          !statusModal.inactivationDescription.trim()
        ) {
          setStatusError('Please provide a description for the inactivation reason.');
          return;
        }

        if (requiresDocument && !statusModal.document) {
          setStatusError('Please upload the relieving or leave document.');
          return;
        }
      }

      setStatusSubmitting(true);

      try {
        const statusValue = isActive ? 'active' : 'inactive';
        const payload = { status: statusValue };

        if (isActive) {
          payload.activationReason = statusModal.activationReason;
          payload.activationDescription =
            statusModal.activationReason === 'others'
              ? statusModal.activationDescription
              : statusModal.activationDescription || null;
          payload.activationDate = statusModal.activationDate;
          payload.effectiveDate = statusModal.activationDate;
        }

        if (isInactive) {
          payload.inactivationReason = statusModal.inactivationReason;
          payload.inactivationDescription =
            statusModal.inactivationReason === 'others'
              ? statusModal.inactivationDescription
              : statusModal.inactivationDescription || null;
          payload.inactivationDate = statusModal.inactivationDate;
          payload.relievingDate = statusModal.inactivationDate;
          payload.leaveFromDate =
            statusModal.inactivationReason === 'long_leave' ? statusModal.leaveFromDate : null;
        }

        let requestBody = payload;

        if (requiresDocument && statusModal.document) {
          const formData = new FormData();
          Object.entries(payload).forEach(([key, value]) => {
            if (value !== null && value !== undefined && value !== '') {
              formData.append(key, value);
            }
          });
          formData.append('relievingDocument', statusModal.document);
          requestBody = formData;
        }

        await apiRequest(
          `/api/admin/employees/${statusModal.employee.id}/status`,
          {
            method: 'PATCH',
            body: requestBody,
          },
          navigate,
        );

        await loadEmployees();
        closeStatusModal();
      } catch (submitError) {
        setStatusError(submitError.message || 'Failed to update employee status');
      } finally {
        setStatusSubmitting(false);
      }
    },
    [statusModal, navigate, loadEmployees, closeStatusModal],
  );


  const validateEmployeeFileUpload = useCallback(() => {
    const selectedEmployee = fileUploadModal.employee;
    const employeeId = Number(selectedEmployee?.id);
    const documentTypeId = Number(fileUploadForm.documentTypeId);
    const selectedFile = fileUploadForm.file;

    if (!Number.isInteger(employeeId) || employeeId <= 0) {
      return 'Please select a valid employee.';
    }

    if (!Number.isInteger(documentTypeId) || documentTypeId <= 0) {
      return 'Please select a document type.';
    }

    if (!selectedFile) {
      return 'Please choose a file.';
    }

    const extension = (selectedFile.name || '').split('.').pop()?.toLowerCase() || '';
    const hasAllowedMimeType = !selectedFile.type || ALLOWED_EMPLOYEE_FILE_MIME_TYPES.includes(selectedFile.type);
    const hasAllowedExtension = ALLOWED_EMPLOYEE_FILE_EXTENSIONS.includes(extension);

    if (!hasAllowedMimeType || !hasAllowedExtension) {
      return 'Only PDF, JPG, JPEG and PNG files are allowed.';
    }

    if (selectedFile.size > MAX_EMPLOYEE_FILE_SIZE) {
      return 'File size must not exceed 10 MB.';
    }

    return '';
  }, [fileUploadForm, fileUploadModal.employee]);

  const openFileUploadModal = useCallback((employeeRow) => {
    setFileUploadModal({ open: true, employee: employeeRow });
    setFileUploadForm({ documentTypeId: '', file: null });
    setFileUploadError('');
    setFileUploadSuccess('');
  }, []);

  const closeFileUploadModal = useCallback(() => {
    if (fileUploadSubmitting) {
      return;
    }

    setFileUploadModal({ open: false, employee: null });
    setFileUploadForm({ documentTypeId: '', file: null });
    setFileUploadError('');
  }, [fileUploadSubmitting]);

  const handleFileUploadSubmit = useCallback(
    async (event) => {
      event.preventDefault();
      if (fileUploadSubmitting) {
        return;
      }

      setFileUploadError('');
      setFileUploadSuccess('');

      const validationError = validateEmployeeFileUpload();
      if (validationError) {
        setFileUploadError(validationError);
        return;
      }

      const selectedEmployee = fileUploadModal.employee;
      const formData = new FormData();
      formData.append('documentTypeId', fileUploadForm.documentTypeId);
      formData.append('file', fileUploadForm.file);

      setFileUploadSubmitting(true);
      try {
        await apiRequest(
          `/api/admin/employees/${selectedEmployee.id}/documents`,
          {
            method: 'POST',
            body: formData,
          },
          navigate,
        );
        setFileUploadModal({ open: false, employee: null });
        setFileUploadForm({ documentTypeId: '', file: null });
        setFileUploadSuccess('Employee document uploaded successfully.');
      } catch (uploadError) {
        setFileUploadError(uploadError.message || 'Failed to upload employee document.');
      } finally {
        setFileUploadSubmitting(false);
      }
    },
    [fileUploadForm, fileUploadModal.employee, fileUploadSubmitting, navigate, validateEmployeeFileUpload],
  );

  const tableRows = useMemo(
    () =>
      employees.map((employee) => {
        const statusKey = normalizeStatus(employee.status || employee.statusKey || 'inactive');
        const statusLabel = statusKey === 'active' ? 'Active' : statusKey === 'pending' ? 'Pending' : 'Inactive';
        const outletName = typeof employee?.outlet === 'string'
          ? employee.outlet
          : employee?.outlet?.name || employee?.outlet?.branchName || '';

        return {
          id: employee.id,
          tempId: employee.tempId || '',
          permId: employee.permId || '',
          name: employee.name || '',
          phone: employee.phone || '',
          designation: employee.designation || '',
          aadhar: employee.aadhar || '',
          outlet: outletName,
          outletId: employee?.outlet?.id || null,
          status: statusLabel,
          statusKey,
          dateOfJoin: employee.dateOfJoin || '',
          relievingDate: employee.relievingDate || '',
          inactiveReason: employee.inactiveReason || '',
          inactiveNotes: employee.inactiveNotes || '',
        };
      }),
    [employees],
  );

  const totalPages =
    pagination.totalPages ||
    (pagination.totalRecords > 0
      ? Math.ceil(pagination.totalRecords / (pagination.pageSize || pageSize))
      : 0);

  const currentPageDisplay = totalPages === 0 ? 0 : pagination.currentPage || currentPage;
  const serialOffset = Math.max(0, (currentPageDisplay - 1) * (pagination.pageSize || pageSize));

  useEffect(() => {
    if (totalPages > 0 && currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const actionButtonGroupClass = useMemo(
    () =>
      [
        'd-flex',
        'flex-column',
        'flex-md-row',
        'justify-content-md-end',
        'align-items-stretch',
        'align-items-md-center',
        'gap-3',
        'mb-4',
      ].join(' '),
    [],
  );

  const hasFilters = Boolean(searchTerm.trim() || selectedOutlet || statusFilter !== 'active' || hasJoinFilter);
  const canListEmployees = employeeListPermissions.view;
  const canCreateEmployee = employeeCreateEditPermissions.create;
  const canEditEmployee = employeeCreateEditPermissions.edit;
  const canViewEmployee = employeeViewPermissions.view;
  const canManageStatus = employeeCreateEditPermissions.edit;
  const canViewApprovals = approvalPendingPermissions.view;
  const canDirectUploadEmployeeFile = isDirectEmployeeAdmin() && employeeFilePermissions.create;
  const canEditApprovals = approvalPendingPermissions.edit;
  const canApproveEmployees = approvalPendingPermissions.approve;

  if (!canListEmployees) {
    return (
      <DashboardLayout title="All Employees">
        <div className="container py-4">You do not have permission to view employee listings.</div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="All Employees">
      <div className="files-page employees-page container-fluid py-4">
        <div className={actionButtonGroupClass}>
          {canCreateEmployee && (
            <Link
              to="/add-employee"
              className="files-pill-button d-flex align-items-center justify-content-center"
            >
              <PersonPlus className="me-2" size={18} />
              Add New Employee
            </Link>
          )}
          <button
            type="button"
            className="files-pill-button d-flex align-items-center justify-content-center"
            onClick={loadEmployees}
            disabled={loading}
          >
            <ArrowClockwise className="me-2" size={18} />
            {loading ? 'Refreshing...' : 'Refresh List'}
          </button>
        </div>

        <div className="files-filters shadow-sm mb-4">
          {hasJoinFilter && <div className="alert alert-info d-flex justify-content-between align-items-center mb-3"><span>Joining Month: <strong>{joinFilterLabel}</strong></span><button type="button" className="btn btn-sm btn-outline-primary" onClick={clearJoinFilter}>Clear joining filter</button></div>}
          <div className="row g-3 align-items-end">
            <div className="col-md-5">
              <label className="form-label text-muted small fw-semibold text-uppercase">
                Search Employees
              </label>
              <div className="files-search-control">
                <Search className="search-icon" size={18} />
                <input
                  type="text"
                  className="form-control files-input"
                  placeholder="Search by name, ID, phone, email..."
                  value={searchTerm}
                  onChange={(event) => {
                    setSearchTerm(event.target.value);
                    setCurrentPage(1);
                  }}
                />
              </div>
            </div>
            <div className="col-md-3">
              <label className="form-label text-muted small fw-semibold text-uppercase">
                Filter by Outlet
              </label>
              <div className="position-relative">
                <input
                  type="text"
                  className="form-control files-input"
                  placeholder="Type to search outlets"
                  value={outletSearchTerm}
                  onChange={(event) => {
                    const value = event.target.value;
                    setOutletSearchTerm(value);
                    setOutletSelectionLocked(false);
                    if (selectedOutlet) {
                      setSelectedOutlet('');
                      setCurrentPage(1);
                    }
                  }}
                  onFocus={() => {
                    if (!isOutletSelectionLocked && outletSearchTerm.trim().length >= 2) {
                      searchOutlets(outletSearchTerm);
                    }
                  }}
                  aria-label="Filter employees by outlet"
                />
                {isOutletSearching && (
                  <div className="position-absolute top-50 end-0 translate-middle-y pe-3 text-muted small">
                    Searching...
                  </div>
                )}
                {!isOutletSelectionLocked && outletOptions.length > 0 && (
                  <div
                    className="dropdown-menu show w-100"
                    style={{ maxHeight: '220px', overflowY: 'auto' }}
                  >
                    {outletOptions.map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        className="dropdown-item text-wrap"
                        onMouseDown={(event) => {
                          event.preventDefault();
                          setSelectedOutlet(option.value);
                          setOutletSearchTerm(option.label);
                          setCurrentPage(1);
                          clearOutletOptions();
                          setOutletSelectionLocked(true);
                        }}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div className="form-text mt-1">
                Type at least 2 characters to search and select an outlet.
              </div>
              {outletSearchError && (
                <div className="text-danger small mt-1">{outletSearchError}</div>
              )}
            </div>
            <div className="col-md-2">
              <label className="form-label text-muted small fw-semibold text-uppercase">
                Employee Status
              </label>
              <div className="btn-group w-100" role="group" aria-label="Employee status filter">
                <button
                  type="button"
                  className={`btn btn-${statusFilter === 'active' ? 'primary' : 'outline-primary'}`}
                  onClick={() => {
                    setStatusFilter('active');
                    const next = new URLSearchParams(searchParams); next.set('status', 'active'); setSearchParams(next, { replace: true });
                    setCurrentPage(1);
                  }}
                >
                  Active
                </button>
                <button
                  type="button"
                  className={`btn btn-${statusFilter === 'inactive' ? 'primary' : 'outline-primary'}`}
                  onClick={() => {
                    setStatusFilter('inactive');
                    const next = new URLSearchParams(searchParams); next.set('status', 'inactive'); setSearchParams(next, { replace: true });
                    setCurrentPage(1);
                  }}
                >
                  Inactive
                </button>
                {hasJoinFilter && (
                  <button
                    type="button"
                    className={`btn btn-${statusFilter === 'all' ? 'primary' : 'outline-primary'}`}
                    onClick={() => {
                      setStatusFilter('all');
                      const next = new URLSearchParams(searchParams); next.set('status', 'all'); setSearchParams(next, { replace: true });
                      setCurrentPage(1);
                    }}
                  >
                    All
                  </button>
                )}
              </div>
            </div>
            <div className="col-md-2">
              <button
                type="button"
                className="employees-clear-button w-100"
                onClick={() => {
                  setSearchTerm('');
                  setSelectedOutlet('');
                  setStatusFilter('active');
                  clearJoinFilter();
                  setCurrentPage(1);
                  setOutletSearchTerm('');
                  clearOutletOptions();
                  setOutletSelectionLocked(false);
                }}
                disabled={!hasFilters}
              >
                Clear Filters
              </button>
            </div>
          </div>
        </div>

        {fileUploadSuccess && (
          <Alert variant="success" className="mb-4" onClose={() => setFileUploadSuccess('')} dismissible>
            {fileUploadSuccess}
          </Alert>
        )}

        <div className="card files-table-card shadow-sm">
          <div className="card-body p-0">
            {loading ? (
              <div className="py-5 text-center">
                <Spinner animation="border" role="status" />
              </div>
            ) : error ? (
              <div className="py-5 px-3">
                <Alert variant="danger" className="mb-0">
                  {error}
                </Alert>
              </div>
            ) : (
              <>
                <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-3 p-3 border-bottom">
                  <div className="d-flex align-items-center gap-2">
                    <label
                      className="text-muted small mb-0 fw-semibold text-uppercase"
                      htmlFor="employee-page-size"
                    >
                      Rows per page
                    </label>
                    <select
                      id="employee-page-size"
                      className="form-select form-select-sm"
                      style={{ maxWidth: '120px' }}
                      value={pageSize}
                      onChange={(event) => {
                        setPageSize(Number(event.target.value));
                        setCurrentPage(1);
                      }}
                    >
                      {[10, 25, 50].map((sizeOption) => (
                        <option key={sizeOption} value={sizeOption}>
                          {sizeOption}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    <button
                      type="button"
                      className="btn btn-outline-secondary btn-sm"
                      onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                      disabled={currentPageDisplay <= 1 || tableRows.length === 0}
                    >
                      Previous
                    </button>
                    <span className="text-muted small fw-semibold">
                      Page {tableRows.length === 0 ? 0 : currentPageDisplay} of {tableRows.length === 0 ? 0 : totalPages}
                    </span>
                    <button
                      type="button"
                      className="btn btn-outline-secondary btn-sm"
                      onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                      disabled={currentPageDisplay >= totalPages || tableRows.length === 0}
                    >
                      Next
                    </button>
                  </div>
                </div>
                <div className="table-responsive">
                  <table className="table table-hover mb-0 files-table align-middle">
                    <thead>
                      <tr>
                        <th style={{ width: '6%' }}>S.No</th>
                        <th>Temporary ID</th>
                        <th>Permanent ID</th>
                        <th>Name</th>
                        <th>Phone</th>
                        <th>Aadhar Number</th>
                        <th>Designation</th>
                        <th>Date of Joining</th>
                        <th>Outlet</th>
                        <th>Status</th>
                        <th className="text-center">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {tableRows.length === 0 ? (
                        <tr>
                          <td colSpan="11" className="text-center py-4">
                            No employees found.
                          </td>
                        </tr>
                      ) : (
                        tableRows.map((row, index) => (
                          <tr key={row.id}>
                            <td className="fw-semibold">{serialOffset + index + 1}</td>
                            <td className="text-uppercase">{row.tempId || '-'}</td>
                            <td className="text-uppercase">{row.permId || '-'}</td>
                            <td className="fw-semibold text-capitalize">{row.name || '-'}</td>
                            <td>{row.phone || '-'}</td>
                            <td>{row.aadhar || '-'}</td>
                            <td className="text-capitalize">{row.designation || '-'}</td>
                            <td>{row.dateOfJoin ? new Date(row.dateOfJoin).toLocaleDateString() : '-'}</td>
                            <td className="text-capitalize">{row.outlet || '-'}</td>
                            <td>
                              <span
                                className={`employees-status-badge ${
                                  row.statusKey === 'active'
                                    ? 'employees-status-active'
                                    : row.statusKey === 'pending'
                                      ? 'employees-status-pending'
                                      : 'employees-status-inactive'
                                }`}
                              >
                                {row.status}
                              </span>
                            </td>
                            <td>
                              <div className="files-action-buttons employees-action-buttons d-flex justify-content-center gap-2">
                                {attendanceReportPermissions.view && (
                                  <button className="btn btn-icon" type="button" title="View Monthly Attendance" aria-label={`View monthly attendance for ${row.name}`} onClick={() => navigate(`/attendance-calendar/${row.id}`, { state: { from: `${window.location.pathname}${window.location.search}` } })}>
                                    <FaCalendarAlt size={16} />
                                  </button>
                                )}
                                {canViewEmployee && (
                                  <button
                                    className="btn btn-icon"
                                    type="button"
                                    aria-label={`View ${row.name}`}
                                    onClick={() => navigate(`/employees/${row.id}`)}
                                  >
                                    <FaEye size={16} />
                                  </button>
                                )}
                                {canDirectUploadEmployeeFile && (
                                  <button
                                    className="btn btn-icon employees-add-file-button"
                                    type="button"
                                    title="Add Employee File"
                                    aria-label={`Add file for ${row.name || 'employee'}`}
                                    onClick={() => openFileUploadModal(row)}
                                  >
                                    <FaFileUpload size={16} />
                                  </button>
                                )}
                                {(canViewApprovals || canEditApprovals || canApproveEmployees) && (
                                  <button
                                    className="btn btn-secondary btn-icon"
                                    type="button"
                                    aria-label={`Open approval view for ${row.name}`}
                                    onClick={() =>
                                      navigate(`/dashboard/employees/${row.id}/approval-view`)
                                    }
                                  >
                                   <FaStamp size={16} />
                                  </button>
                                )}
                                {canEditEmployee && (
                                  (row.statusKey === 'inactive' ? (
                                    <button
                                      type="button"
                                      className="btn btn-icon"
                                      aria-label={`Edit disabled for ${row.name}`}
                                      title="Inactive employees cannot be edited"
                                      disabled
                                    >
                                      <FaEdit size={16} />
                                    </button>
                                  ) : (
                                    <Link
                                      to={`/add-employee?employeeId=${row.id}`}
                                      className="btn btn-icon"
                                      aria-label={`Edit ${row.name}`}
                                    >
                                      <FaEdit size={16} />
                                    </Link>
                                  ))
                                )}
                                {canManageStatus && (
                                  <button
                                    className="btn btn-icon"
                                    type="button"
                                    aria-label={`Change status for ${row.name}`}
                                    onClick={() => openStatusModal(row)}
                                  >
                                    <FaSyncAlt size={16} />
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        </div>
      </div>


      {fileUploadModal.open && (
        <>
          <div className="modal fade show" style={{ display: 'block' }} role="dialog" aria-modal="true">
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Add Employee File</h5>
                  <button type="button" className="btn-close" aria-label="Close" onClick={closeFileUploadModal} disabled={fileUploadSubmitting} />
                </div>
                <form onSubmit={handleFileUploadSubmit}>
                  <div className="modal-body">
                    <div className="employees-upload-employee-summary mb-3">
                      <div><strong>Employee:</strong> {fileUploadModal.employee?.name || '-'}</div>
                      <div><strong>Temporary ID:</strong> {fileUploadModal.employee?.tempId || '-'}</div>
                      <div><strong>Permanent ID:</strong> {fileUploadModal.employee?.permId || '-'}</div>
                      <div><strong>Outlet:</strong> {fileUploadModal.employee?.outlet || '-'}</div>
                      {fileUploadModal.employee?.designation && <div><strong>Designation:</strong> {fileUploadModal.employee.designation}</div>}
                    </div>
                    {fileUploadError && <div className="alert alert-danger" role="alert">{fileUploadError}</div>}
                    {documentTypeError && <div className="alert alert-warning" role="alert">{documentTypeError}</div>}
                    <div className="mb-3">
                      <label className="form-label" htmlFor="employee-file-document-type">Document Type</label>
                      <select
                        id="employee-file-document-type"
                        className="form-select"
                        value={fileUploadForm.documentTypeId}
                        onChange={(event) => setFileUploadForm((prev) => ({ ...prev, documentTypeId: event.target.value }))}
                        disabled={fileUploadSubmitting || isLoadingDocumentTypes}
                        required
                      >
                        <option value="">Select Document Type</option>
                        {documentTypes.map((documentType) => (
                          <option key={documentType.id} value={documentType.id}>{documentType.name}</option>
                        ))}
                      </select>
                    </div>
                    <div className="mb-3">
                      <label className="form-label" htmlFor="employee-file-upload-input">File</label>
                      <input
                        id="employee-file-upload-input"
                        type="file"
                        className="form-control"
                        accept=".pdf,.jpg,.jpeg,.png"
                        onChange={(event) => {
                          const [file] = event.target.files || [];
                          setFileUploadForm((prev) => ({ ...prev, file: file || null }));
                        }}
                        disabled={fileUploadSubmitting}
                        required
                      />
                      <div className="form-text">Allowed formats: PDF, JPG, JPEG and PNG. Maximum size: 10 MB.</div>
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button type="button" className="btn btn-outline-secondary" onClick={closeFileUploadModal} disabled={fileUploadSubmitting}>Cancel</button>
                    <button type="submit" className="btn btn-primary" disabled={fileUploadSubmitting}>{fileUploadSubmitting ? 'Uploading...' : 'Upload'}</button>
                  </div>
                </form>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show" />
        </>
      )}

      {statusModal.open && (
        <>
          <div
            className="modal fade show"
            style={{ display: 'block' }}
            role="dialog"
            aria-modal="true"
          >
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Change Employee Status</h5>
                  <button
                    type="button"
                    className="btn-close"
                    aria-label="Close"
                    onClick={closeStatusModal}
                    disabled={statusSubmitting}
                  />
                </div>
                <form onSubmit={handleStatusSubmit}>
                  <div className="modal-body">
                    <p className="mb-3 text-muted">
                      Update status for <strong>{statusModal.employee?.name || 'employee'}</strong>.
                    </p>
                    {statusError && (
                      <div className="alert alert-danger" role="alert">
                        {statusError}
                      </div>
                    )}
                    <div className="mb-3">
                      <label className="form-label">Status</label>
                      <select
                        className="form-select"
                        value={statusModal.status}
                        onChange={(event) => {
                          const value = event.target.value;
                          setStatusModal((prev) => ({
                            ...prev,
                            status: value,
                            activationReason: value === 'active' ? prev.activationReason || 'rejoined' : prev.activationReason,
                            activationDescription: value === 'active' ? prev.activationDescription : '',
                            activationDate:
                              value === 'active'
                                ? prev.activationDate || getTodayInputValue()
                                : prev.activationDate || getTodayInputValue(),
                            inactivationReason:
                              value === 'inactive' ? prev.inactivationReason || 'relieving' : 'relieving',
                            inactivationDescription:
                              value === 'inactive' ? prev.inactivationDescription : '',
                            inactivationDate:
                              value === 'inactive' && prev.inactivationReason === 'relieving'
                                ? prev.inactivationDate
                                : '',
                            leaveFromDate:
                              value === 'inactive' && prev.inactivationReason === 'long_leave'
                                ? prev.leaveFromDate
                                : '',
                            document: value === 'inactive' ? prev.document : null,
                          }));
                        }}
                      >
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                      </select>
                    </div>
                    {statusModal.status === 'active' && (
                      <>
                        <div className="mb-3">
                          <label className="form-label">Activation Reason</label>
                          <select
                            className="form-select"
                            value={statusModal.activationReason}
                            onChange={(event) => {
                              const value = event.target.value;
                              setStatusModal((prev) => ({
                                ...prev,
                                activationReason: value,
                                activationDescription:
                                  value === 'others' ? prev.activationDescription : '',
                              }));
                            }}
                            required
                          >
                            <option value="rejoined">Rejoined</option>
                            <option value="after_long_leave">After Long Leave</option>
                            <option value="others">Others</option>
                          </select>
                        </div>
                        <div className="mb-3">
                          <label className="form-label">Activation Date</label>
                          <input
                            type="date"
                            className="form-control"
                            value={statusModal.activationDate}
                            onChange={(event) =>
                              setStatusModal((prev) => ({
                                ...prev,
                                activationDate: event.target.value,
                              }))
                            }
                            required
                          />
                        </div>
                        {statusModal.activationReason === 'others' && (
                          <div className="mb-3">
                            <label className="form-label">Activation Description</label>
                            <textarea
                              className="form-control"
                              rows={3}
                              value={statusModal.activationDescription}
                              onChange={(event) =>
                                setStatusModal((prev) => ({
                                  ...prev,
                                  activationDescription: event.target.value,
                                }))
                              }
                              placeholder="Describe the activation reason"
                              required
                            />
                          </div>
                        )}
                      </>
                    )}
                    {statusModal.status === 'inactive' && (
                      <>
                        <div className="mb-3">
                          <label className="form-label">Inactivation Reason</label>
                          <select
                            className="form-select"
                            value={statusModal.inactivationReason}
                            onChange={(event) => {
                              const value = event.target.value;
                              setStatusModal((prev) => ({
                                ...prev,
                                inactivationReason: value,
                                inactivationDescription: value === 'others' ? prev.inactivationDescription : '',
                                inactivationDate: value === 'relieving' ? prev.inactivationDate : '',
                                leaveFromDate: value === 'long_leave' ? prev.leaveFromDate : '',
                                document: value === 'others' ? null : prev.document,
                              }));
                            }}
                          >
                            <option value="relieving">Relieving</option>
                            <option value="long_leave">Long Leave</option>
                            <option value="others">Others</option>
                          </select>
                        </div>
                        <div className="mb-3">
                          <label className="form-label">Inactivation Date</label>
                          <input
                            type="date"
                            className="form-control"
                            value={statusModal.inactivationDate}
                            onChange={(event) =>
                              setStatusModal((prev) => ({
                                ...prev,
                                inactivationDate: event.target.value,
                              }))
                            }
                            required
                          />
                        </div>
                        {statusModal.inactivationReason === 'long_leave' && (
                          <div className="mb-3">
                            <label className="form-label">Leave From Date</label>
                            <input
                              type="date"
                              className="form-control"
                              value={statusModal.leaveFromDate}
                              onChange={(event) =>
                                setStatusModal((prev) => ({
                                  ...prev,
                                  leaveFromDate: event.target.value,
                                }))
                              }
                              required
                            />
                          </div>
                        )}
                        {statusModal.inactivationReason !== 'others' && (
                          <div className="mb-3">
                            <label className="form-label">Upload Relieving / Long Leave Document</label>
                            <input
                              type="file"
                              className="form-control"
                              accept="application/pdf,image/*"
                              onChange={(event) => {
                                const [file] = event.target.files || [];
                                setStatusModal((prev) => ({ ...prev, document: file || null }));
                              }}
                            />
                            <small className="text-muted">Accepted formats: PDF or image files</small>
                          </div>
                        )}
                        <div className="mb-3">
                          <label className="form-label">Notes</label>
                          <textarea
                            className="form-control"
                            rows={3}
                            value={statusModal.inactivationDescription}
                            onChange={(event) =>
                              setStatusModal((prev) => ({
                                ...prev,
                                inactivationDescription: event.target.value,
                              }))
                            }
                            placeholder="Add additional details if needed"
                          />
                        </div>
                      </>
                    )}
                  </div>
                  <div className="modal-footer">
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={closeStatusModal}
                      disabled={statusSubmitting}
                    >
                      Cancel
                    </button>
                    <button type="submit" className="btn btn-danger" disabled={statusSubmitting}>
                      {statusSubmitting ? 'Saving...' : 'Update Status'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show" />
        </>
      )}
    </DashboardLayout>
  );
};

export default Employees;
